import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useApp, apiFetch } from "../../context/AppContext";
import {
  Card,
  Badge,
  Button,
  Modal,
  Input,
  Select,
  DashboardStatCard,
  ProgressBar,
  EmptyState,
  Pagination,
  FormErrorsSummary,
} from "../../components/common/UI";
import SmtpSettingsPanel from "../../components/common/SmtpSettingsPanel";
import AiProviderSettingsPanel from "../../components/common/AiProviderSettingsPanel";
import BreachSettingsPanel from "../../components/common/BreachSettingsPanel";
import BreachTemplatesPanel from "../../components/common/BreachTemplatesPanel";
import DataExportPanel from "../../components/common/DataExportPanel";
import NotificationSettingsPanel from "../../components/common/NotificationSettingsPanel";
import CollapsibleSettingsCard from "../../components/common/CollapsibleSettingsCard";
import GlobalComplianceSettingsPanel from "../../components/common/GlobalComplianceSettingsPanel";
import DelegateAccessSection from "../../components/common/DelegateAccessSection";
import UserActivityLogModal from "../../components/common/UserActivityLogModal";
import {
  Users,
  Search,
  Package,
  AlertTriangle,
  UserPlus,
  ListPlus,
  Upload,
  Download,
  FileSpreadsheet,
  ChevronRight,
  ChevronDown,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Shield,
  Calendar,
  Mail,
  Key,
  CheckCircle2,
  XCircle,
  X,
  Building2,
  BarChart3,
  TrendingUp,
  Clock,
  UserCheck,
  UserX,
  RefreshCw,
  Activity,
  Lock,
  Unlock,
  MoreVertical,
  Database,
  Zap,
  Phone,
  Settings,
  Eye,
  EyeOff,
  Sparkles,
  Plus,
  Network,
  ScrollText,
} from "lucide-react";

// ─── apiFetch helper (falls back gracefully if not exported from context) ────
const _fetch =
  typeof apiFetch === "function"
    ? apiFetch
    : (path, opts) =>
        fetch(`/api/v1${path}`, {
          headers: { "Content-Type": "application/json" },
          ...opts,
        });

// ─── useTenantDashboard ───────────────────────────────────────────────────────
function useTenantDashboard() {
  const { showNotification } = useApp();
  const [data, setData] = useState(null);
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [dashRes, licRes] = await Promise.all([
        _fetch("/tenant/dashboard"),
        _fetch("/tenant/license"),
      ]);
      if (dashRes.ok) setData(await dashRes.json());
      if (licRes.ok) setLicense(await licRes.json());
    } catch {
      showNotification("Failed to load tenant data", "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    refresh();
  }, [refresh]);
  return { data, license, loading, refresh };
}

// ─── Avatar helper ────────────────────────────────────────────────────────────
function Avatar({ name = "", size = "md" }) {
  const letter = (name || "?")[0].toUpperCase();
  const colors = [
    "from-blue-500 to-indigo-600",
    "from-violet-500 to-purple-600",
    "from-emerald-500 to-teal-600",
    "from-amber-500 to-orange-600",
    "from-rose-500 to-pink-600",
  ];
  const color = colors[letter.charCodeAt(0) % colors.length];
  const sz =
    size === "sm"
      ? "w-8 h-8 text-xs"
      : size === "lg"
        ? "w-12 h-12 text-base"
        : "w-9 h-9 text-sm";
  return (
    <div
      className={`${sz} rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white font-bold flex-shrink-0 shadow-sm`}
    >
      {letter}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TenantDashboard
// ═══════════════════════════════════════════════════════════════════════════════
export function TenantDashboard() {
  const { currentUser, showNotification } = useApp();
  const { data, license, loading, refresh } = useTenantDashboard();
  const [recentUsers, setRecentUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setUsersLoading(true);
      try {
        const res = await _fetch("/tenant/users?page=1&page_size=5");
        const json = await res.json();
        setRecentUsers(json.items || []);
      } catch {
        showNotification("Failed to load recent users", "error");
      } finally {
        setUsersLoading(false);
      }
    })();
  }, [showNotification]);

  const used = license?.users_created ?? data?.users_created ?? 0;
  const limit = license?.user_limit ?? data?.user_limit ?? 1;
  const pct = Math.round((used / limit) * 100);
  const plan = license?.plan ?? data?.license_plan ?? "—";
  const expiresAt = license?.expires_at ?? data?.license_expires_at ?? "—";
  const active = data?.active_users ?? 0;
  const total = data?.total_users ?? 0;

  const stats = [
    {
      label: "Total Users",
      tooltip:
        "Everyone who has an account in this organisation, including how many are currently active.",
      value: total,
      sub: `${active} active right now`,
      icon: <Users size={18} />,
      gradient: "bg-gradient-to-br from-blue-400 to-indigo-800",
    },
    {
      label: "User Limit",
      tooltip: "The maximum number of user accounts allowed by your license plan.",
      value: limit,
      sub: plan + " plan",
      icon: <Package size={18} />,
      gradient: "bg-gradient-to-br from-violet-400 to-purple-800",
    },
    {
      label: "Slots Used",
      tooltip: "Share of your licensed user seats that are already taken.",
      value: `${pct}%`,
      sub: `${used} of ${limit} users`,
      icon: <Activity size={18} />,
      gradient:
        pct > 80
          ? "bg-gradient-to-br from-red-400 to-red-800"
          : "bg-gradient-to-br from-emerald-500 to-teal-600",
    },
    {
      label: "License Expiry",
      tooltip:
        "The date your organisation's license ends, and whether it is currently active.",
      value: expiresAt || "N/A",
      sub: license?.is_active ? "License active" : "License inactive",
      icon: <Calendar size={18} />,
      gradient: "bg-gradient-to-br from-sky-400 to-blue-800",
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Header ── */}
      <div
        data-tour-id="tour-tenant-header"
        className="flex items-start justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
            <Building2 size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Tenant Dashboard
            </h1>
            <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-0.5">
              <Shield size={12} />
              {currentUser?.full_name ?? currentUser?.email ?? "Tenant Admin"} ·
              Overview
            </p>
          </div>
        </div>
        <button
          data-tour-id="tour-tenant-refresh"
          onClick={refresh}
          disabled={loading}
          className="p-2.5 rounded-xl text-slate-400 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 transition-all disabled:opacity-40"
          title="Refresh"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* ── Stats row ── */}
      <div
        data-tour-id="tour-tenant-dashboard"
        className="grid grid-cols-2 lg:grid-cols-4 gap-3"
      >
        {stats.map((s, i) => (
          <DashboardStatCard key={i} {...s} loading={loading} />
        ))}
      </div>

      {/* ── Quick Actions Grid ── */}
      <div
        data-tour-id="tour-tenant-quick-actions"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        {[
          {
            icon: UserPlus,
            label: "Add New User",
            color: "text-blue-600",
            bg: "hover:bg-blue-50/40 hover:border-blue-200",
            to: "/tenant/users",
          },
          {
            icon: Mail,
            label: "Email Template",
            color: "text-rose-500",
            bg: "hover:bg-rose-50/40 hover:border-rose-200",
            to: "/tenant/email-templates",
          },
          {
            icon: Package,
            label: "License Info",
            color: "text-violet-600",
            bg: "hover:bg-violet-50/40 hover:border-violet-200",
            to: "/tenant/license",
          },
          {
            icon: Settings,
            label: "Settings",
            color: "text-slate-600",
            bg: "hover:bg-slate-50/40 hover:border-slate-200",
            to: "/tenant/settings",
          },
        ].map((a, i) => {
          const Icon = a.icon;
          return (
            <Link
              key={i}
              to={a.to}
              data-tour-id={
                a.to === "/tenant/users"
                  ? "tour-tenant-qa-users"
                  : a.to === "/tenant/email-templates"
                    ? "tour-tenant-qa-email"
                    : a.to === "/tenant/license"
                      ? "tour-tenant-qa-license"
                      : "tour-tenant-qa-settings"
              }
              className={`bg-white border rounded-xl p-4 hover:shadow-md transition-all flex items-center gap-3 ${a.bg}`}
            >
              <span
                className={`p-2 rounded-lg bg-slate-50 shadow-sm ${a.color}`}
              >
                <Icon size={16} />
              </span>
              <span className="text-sm font-semibold text-slate-800">
                {a.label}
              </span>
              <ChevronRight className="ml-auto text-slate-300" size={14} />
            </Link>
          );
        })}
      </div>

      {/* ── Main content ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Recent Users */}
        <div className="xl:col-span-2" data-tour-id="tour-tenant-recent-users">
          <Card padding={false}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <Link
                to="/tenant/users"
                className="flex items-center gap-2 group/title"
              >
                <div className="p-1.5 rounded-lg bg-blue-50">
                  <Users size={15} className="text-blue-600" />
                </div>
                <h2 className="text-sm font-semibold text-slate-800 group-hover/title:text-blue-700">
                  Recent Users
                </h2>
                <ChevronRight size={14} className="text-slate-300 group-hover/title:text-blue-400" />
              </Link>
              <Link
                to="/tenant/users"
                className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold hover:bg-blue-100"
              >
                <UserCheck size={11} />
                {total} total
              </Link>
            </div>

            <div className="p-4">
              {usersLoading ? (
                <div className="flex items-center justify-center py-10 gap-2 text-slate-400 text-sm">
                  <RefreshCw size={14} className="animate-spin" /> Loading
                  users…
                </div>
              ) : recentUsers.length === 0 ? (
                <EmptyState
                  icon={<Users size={40} />}
                  title="No users yet"
                  description="Create your first user to get started."
                />
              ) : (
                <div className="w-full max-w-full overflow-auto scrollbar-thin max-h-[calc(5*4.25rem)]">
                  <div className="space-y-2">
                    {recentUsers.map((u) => (
                      <Link
                        key={u.id}
                        to="/tenant/users"
                        className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-100 hover:border-blue-100 hover:bg-blue-50/30 transition-all group"
                      >
                        <Avatar name={u.full_name || u.email} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            {u.full_name}
                          </p>
                          <p className="text-xs text-slate-400 flex items-center gap-1 truncate">
                            <Mail size={10} />
                            {u.email}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
                          ${u.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}
                          >
                            {u.status === "active" ? (
                              <CheckCircle2 size={10} />
                            ) : (
                              <XCircle size={10} />
                            )}
                            {u.status}
                          </span>
                          <ChevronRight
                            size={14}
                            className="text-slate-300 group-hover:text-blue-400 transition-colors"
                          />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* License panel */}
        <div className="space-y-4" data-tour-id="tour-tenant-license-panel">
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 rounded-lg bg-violet-50">
                <Database size={15} className="text-violet-600" />
              </div>
              <h3 className="text-sm font-semibold text-slate-800">
                License Utilization
              </h3>
            </div>

            {/* Donut-style usage */}
            <div className="flex items-center gap-4 mb-4">
              <div className="relative w-20 h-20 flex-shrink-0">
                <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                  <circle
                    cx="18"
                    cy="18"
                    r="15.5"
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="3"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="15.5"
                    fill="none"
                    stroke={
                      pct > 90 ? "#ef4444" : pct > 70 ? "#f59e0b" : "#3b82f6"
                    }
                    strokeWidth="3"
                    strokeDasharray={`${pct * 0.973} 97.3`}
                    strokeLinecap="round"
                    style={{ transition: "stroke-dasharray 0.8s ease" }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-bold text-slate-800">
                    {pct}%
                  </span>
                </div>
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {used}
                  <span className="text-slate-400 text-base font-normal">
                    /{limit}
                  </span>
                </p>
                <p className="text-xs text-slate-500 mt-0.5">users created</p>
                <p className="text-xs text-slate-400 mt-1">
                  {limit - used} slots remaining
                </p>
              </div>
            </div>

            <ProgressBar
              value={used}
              max={limit}
              color={pct > 90 ? "red" : pct > 70 ? "amber" : "primary"}
            />

            <div className="mt-4 space-y-2.5">
              {[
                {
                  icon: <Zap size={13} />,
                  label: "Plan",
                  value: plan,
                  color: "text-violet-600 bg-violet-50",
                },
                {
                  icon: <Shield size={13} />,
                  label: "Status",
                  value: license?.is_active ? "Active" : "Inactive",
                  color: license?.is_active
                    ? "text-emerald-600 bg-emerald-50"
                    : "text-slate-500 bg-slate-100",
                },
                {
                  icon: <Calendar size={13} />,
                  label: "Expires",
                  value: String(expiresAt || "—"),
                  color: "text-blue-600 bg-blue-50",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0"
                >
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className={`p-1 rounded ${item.color}`}>
                      {item.icon}
                    </span>
                    {item.label}
                  </div>
                  <span className="text-xs font-semibold text-slate-700">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TenantUsersPage
// ═══════════════════════════════════════════════════════════════════════════════
const EMPTY_FORM = {
  full_name: "",
  email: "",
  password: "",
  phone: "",
  department_ids: [],
  access_role: "L1",
  is_spoc: false,
  spoc_department_id: "",
};

function userAssignedDepartmentIds(user) {
  const ids = [];
  const seen = new Set();
  for (const raw of [user?.department_id, ...(user?.department_ids || [])]) {
    if (!raw) continue;
    const id = String(raw);
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

function formatUserDepartments(user, departments) {
  const ids = userAssignedDepartmentIds(user);
  const names = ids
    .map((id) => departments.find((d) => String(d.id) === id)?.name)
    .filter(Boolean);
  if (names.length) return names.join(", ");
  return user?.department_name || "";
}

function selectionIsSpocHead(isSpoc, spocDepartmentId, headDepartmentId) {
  return Boolean(
    isSpoc &&
      spocDepartmentId &&
      headDepartmentId &&
      String(spocDepartmentId) === String(headDepartmentId),
  );
}

function applySpocHeadRole(next, headDepartmentId) {
  if (
    selectionIsSpocHead(next.is_spoc, next.spoc_department_id, headDepartmentId)
  ) {
    return { ...next, access_role: "L1" };
  }
  return next;
}

function headDepartmentIdFor(departments, user) {
  const listed = (departments || []).find((d) => d.is_head)?.id;
  return listed || user?.head_department_id || "";
}

/** Compact SPOC toggle used on create + edit user forms. */
function SpocFields({
  departments,
  departmentIds,
  isSpoc,
  spocDepartmentId,
  onIsSpocChange,
  onSpocDepartmentChange,
  headDepartmentId,
}) {
  const assigned = (departments || []).filter((d) =>
    (departmentIds || []).map(String).includes(String(d.id)),
  );
  const selectedIsHead =
    isSpoc &&
    spocDepartmentId &&
    headDepartmentId &&
    String(spocDepartmentId) === String(headDepartmentId);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 space-y-2.5">
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          checked={!!isSpoc}
          onChange={(e) => {
            const checked = e.target.checked;
            onIsSpocChange(checked);
            if (!checked) onSpocDepartmentChange("");
            else if (!spocDepartmentId && assigned.length === 1) {
              onSpocDepartmentChange(String(assigned[0].id));
            }
          }}
        />
        <span>
          <span className="block text-sm font-medium text-slate-800">
            Mark as SPOC
          </span>
          <span className="block text-xs text-slate-500 mt-0.5 leading-relaxed">
            One SPOC per department. If their SPOC department is the Head
            Department, they become SPOC Head with access across all
            departments.
          </span>
        </span>
      </label>

      {isSpoc && (
        <div className="pl-6 space-y-2">
          <select
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={spocDepartmentId || ""}
            onChange={(e) => onSpocDepartmentChange(e.target.value)}
          >
            <option value="">Select SPOC department…</option>
            {assigned.map((d) => (
              <option key={d.id} value={d.id}>
                {d.code ? `${d.code} — ` : ""}
                {d.name}
                {d.is_head ? " (Head Department)" : ""}
              </option>
            ))}
          </select>
          {assigned.length === 0 && (
            <p className="text-xs text-amber-700">
              Assign at least one department before marking SPOC.
            </p>
          )}
          {selectedIsHead && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 leading-relaxed">
              <span className="font-semibold">SPOC Head</span> — this user will
              have global access across all departments, and can delegate that
              access temporarily to other SPOCs.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DepartmentMultiSelect({
  departments,
  value,
  onChange,
  error,
  disabled = false,
  hint,
}) {
  const selected = Array.isArray(value) ? value.map(String) : [];
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onPointer = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, []);

  const toggle = (id) => {
    const key = String(id);
    if (selected.includes(key)) onChange(selected.filter((x) => x !== key));
    else onChange([...selected, key]);
  };

  const selectedDepts = departments.filter((d) =>
    selected.includes(String(d.id)),
  );
  const q = query.trim().toLowerCase();
  const filtered = departments.filter((d) => {
    if (!q) return true;
    return `${d.code} ${d.name}`.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-1.5" ref={ref}>
      <label className="block text-sm font-medium text-slate-700">
        Department
        {selected.length > 0 && (
          <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold">
            {selected.length}
          </span>
        )}
      </label>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (!disabled) setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className={`w-full rounded-lg border bg-white text-sm text-left px-3.5 py-2.5 cursor-pointer
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all
          ${error ? "border-red-300" : "border-slate-200"} ${disabled ? "bg-slate-50 cursor-not-allowed" : ""}`}
      >
        <span className="flex items-start gap-2">
          <span className="flex-1 flex flex-wrap gap-1.5 min-h-[22px]">
            {selectedDepts.length === 0 && (
              <span className="text-slate-400 leading-[22px]">
                Select department
              </span>
            )}
            {selectedDepts.map((d) => (
              <span
                key={d.id}
                className="inline-flex items-center gap-1 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold pl-2 pr-1 py-0.5"
              >
                {d.code} — {d.name}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) toggle(d.id);
                  }}
                  className="p-0.5 rounded hover:bg-indigo-100 text-indigo-400 hover:text-indigo-700"
                  aria-label={`Remove ${d.name}`}
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </span>
          <ChevronDown
            size={16}
            className={`text-slate-400 shrink-0 mt-0.5 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </span>
      </div>
      {open && (
        <div className="rounded-lg border border-indigo-200 bg-white shadow-lg overflow-hidden z-50">
          <div className="p-2 border-b border-slate-100">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search departments…"
              className="w-full rounded-md border border-slate-200 bg-slate-50 text-sm px-3 py-1.5 outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="max-h-44 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2.5 text-xs text-slate-400">
                No departments match
              </p>
            ) : (
              filtered.map((d) => {
                const sel = selected.includes(String(d.id));
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => toggle(d.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm ${
                      sel
                        ? "bg-indigo-50 text-indigo-800"
                        : "text-slate-800 hover:bg-slate-50"
                    }`}
                  >
                    <span
                      className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                        sel
                          ? "bg-indigo-600 border-indigo-600 text-white"
                          : "border-slate-300 bg-white"
                      }`}
                    >
                      {sel && <CheckCircle2 size={11} />}
                    </span>
                    <span>
                      {d.code} — {d.name}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
      {error && (
        <p className="text-xs text-red-700 flex items-start gap-1.5 px-2.5 py-2 rounded-lg bg-red-50 border border-red-200 font-medium">
          {error}
        </p>
      )}
      <p className="text-xs text-slate-500">
        {hint || "Select one or more departments from the dropdown."}
      </p>
    </div>
  );
}

const USER_CSV_HEADER_ALIASES = {
  full_name: ["full_name", "name", "fullname", "user_name", "username"],
  email: ["email", "email_address", "e_mail"],
  password: ["password", "temp_password", "initial_password", "passwd"],
  phone: ["phone", "contact_number", "mobile", "phone_number"],
  department: ["department", "department_name", "department_code", "dept"],
  access_role: ["access_role", "role", "level", "access_level"],
};

function normalizeCsvHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function splitCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === "," && !inQuotes) {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

function findCsvColumnIndex(headers, aliases) {
  for (const alias of aliases) {
    const idx = headers.findIndex((header) => header === alias);
    if (idx >= 0) return idx;
  }
  return -1;
}

/** Parse uploaded CSV text for preview and slot checks before import. */
function parseUserCsvText(text, domainName) {
  const lines = String(text || "")
    .trim()
    .split(/\r?\n/)
    .filter((line) => line.trim());
  if (!lines.length) {
    return { rows: [], lineErrors: ["CSV file is empty."] };
  }

  const headers = splitCsvLine(lines[0]).map(normalizeCsvHeader);
  const col = {
    full_name: findCsvColumnIndex(headers, USER_CSV_HEADER_ALIASES.full_name),
    email: findCsvColumnIndex(headers, USER_CSV_HEADER_ALIASES.email),
    password: findCsvColumnIndex(headers, USER_CSV_HEADER_ALIASES.password),
    phone: findCsvColumnIndex(headers, USER_CSV_HEADER_ALIASES.phone),
  };

  if (col.full_name < 0 || col.email < 0 || col.password < 0) {
    return {
      rows: [],
      lineErrors: [
        "CSV must include columns: full_name, email, password (phone optional)",
      ],
    };
  }

  const rows = [];
  const lineErrors = [];
  const seenEmails = new Set();

  lines.slice(1).forEach((line, index) => {
    const cols = splitCsvLine(line);
    if (!cols.some((value) => value.trim())) return;

    const rowNum = index + 2;
    const full_name = cols[col.full_name] || "";
    const email = cols[col.email] || "";
    const password = cols[col.password] || "";
    const phone = col.phone >= 0 ? cols[col.phone] || "" : "";

    if (!full_name || full_name.length < 2) {
      lineErrors.push(
        `Row ${rowNum}: full name is required (min. 2 characters)`,
      );
      return;
    }
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      lineErrors.push(`Row ${rowNum}: valid email required`);
      return;
    }
    if (!domainName) {
      lineErrors.push(`Row ${rowNum}: tenant domain is not configured`);
      return;
    }
    const domainErr = userEmailDomainError(email, domainName);
    if (domainErr) {
      lineErrors.push(`Row ${rowNum}: ${domainErr}`);
      return;
    }
    const emailKey = email.trim().toLowerCase();
    if (seenEmails.has(emailKey)) {
      lineErrors.push(`Row ${rowNum}: duplicate email "${email}" in file`);
      return;
    }
    seenEmails.add(emailKey);
    if (!password || password.length < 8) {
      lineErrors.push(`Row ${rowNum}: password must be at least 8 characters`);
      return;
    }
    if (phone && !/^[0-9]{10}$/.test(phone)) {
      lineErrors.push(`Row ${rowNum}: phone must be a 10-digit number`);
      return;
    }

    rows.push({
      full_name,
      email,
      password,
      ...(phone ? { phone } : {}),
    });
  });

  if (!rows.length && !lineErrors.length) {
    lineErrors.push("No user rows found below the header row.");
  }

  return { rows, lineErrors };
}

function isUserSpreadsheetFile(file) {
  const name = (file?.name || "").toLowerCase();
  return name.endsWith(".csv") || name.endsWith(".xlsx");
}

function previewDomainUserId(domain, email) {
  const d = (domain || "").trim().toLowerCase();
  const e = (email || "").trim().toLowerCase();
  if (!d || !e) return "";
  return `${d}:${e}`;
}

function emailMatchesTenantDomain(email, domain) {
  const e = (email || "").trim().toLowerCase();
  const d = (domain || "").trim().toLowerCase();
  if (!e || !d || !e.includes("@")) return false;
  const emailDomain = e.split("@")[1];
  return emailDomain === d || emailDomain.endsWith(`.${d}`);
}

function userEmailDomainError(email, domain) {
  const d = (domain || "").trim().toLowerCase();
  if (!d || !email || !/\S+@\S+\.\S+/.test(email)) return null;
  if (!emailMatchesTenantDomain(email, d)) {
    return `Email must use the tenant domain (@${d})`;
  }
  return null;
}

export function TenantUsersPage() {
  const { currentUser, showNotification } = useApp();
  const bulkFileRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const PAGE_SIZE = 20;
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkPreviewRows, setBulkPreviewRows] = useState([]);
  const [bulkIsExcel, setBulkIsExcel] = useState(false);
  const [bulkDragOver, setBulkDragOver] = useState(false);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [editUser, setEditUser] = useState(null); // user being edited
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetSaving, setResetSaving] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null); // user being deleted
  const [form, setForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    department_ids: [],
    access_role: "L1",
  });
  const [departments, setDepartments] = useState([]);
  const [accessRoles, setAccessRoles] = useState([
    { key: "L1", label: "L1", grants_all_licensed: true },
    { key: "L2", label: "L2", grants_all_licensed: false },
    { key: "L3", label: "L3", grants_all_licensed: false },
    { key: "L4", label: "L4", grants_all_licensed: false },
    { key: "L5", label: "L5", grants_all_licensed: false },
  ]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState({}); // { [userId]: true } while in-flight
  const [licData, setLicData] = useState(null);

  // Delegate modal state
  const [delegateUser, setDelegateUser] = useState(null); // user being delegated
  const [delegateDates, setDelegateDates] = useState({ start_date: "", end_date: "" });
  const [delegateSaving, setDelegateSaving] = useState(false);

  // Activity log modal state
  const [activityUser, setActivityUser] = useState(null); // user whose activity log is open

  // Current delegate grant — only one user may hold global access at a time
  const [delegateMembers, setDelegateMembers] = useState([]);
  const [revokeTarget, setRevokeTarget] = useState(null); // { member, user }
  const [revoking, setRevoking] = useState(false);

  const fetchDelegateMembers = useCallback(async () => {
    try {
      const res = await _fetch("/head-department/members");
      if (res.ok) {
        const data = await res.json();
        setDelegateMembers(Array.isArray(data) ? data : []);
      }
    } catch {
      /* non-fatal — the Delegate/Revoke buttons just stay hidden */
    }
  }, []);

  useEffect(() => {
    fetchDelegateMembers();
  }, [fetchDelegateMembers]);

  // The single active/scheduled delegate, if any
  const currentDelegate =
    delegateMembers.find(
      (m) => m.status === "active" || m.status === "scheduled",
    ) || null;

  // Load license info, departments, and access roles
  useEffect(() => {
    (async () => {
      try {
        const dashRes = await _fetch("/tenant/dashboard");
        if (dashRes.ok) setLicData(await dashRes.json());
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [deptRes, accessRes] = await Promise.all([
          _fetch("/tenant/departments?page_size=100&include_inactive=false"),
          _fetch("/tenant/settings/module-access"),
        ]);
        if (deptRes.ok) {
          const data = await deptRes.json();
          setDepartments(data.items || []);
        }
        if (accessRes.ok) {
          const data = await accessRes.json();
          if (Array.isArray(data.roles) && data.roles.length) {
            setAccessRoles(data.roles);
          }
        }
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      if (search) params.append("search", search);
      const res = await _fetch(`/tenant/users?${params}`);
      const data = await res.json();

      if (!res.ok) {
        showNotification(
          typeof data.detail === "string"
            ? data.detail
            : JSON.stringify(data.detail),
          "error",
        );

        return;
      }
      setUsers(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch {
      showNotification("Network error", "error");
    } finally {
      setLoading(false);
    }
  }, [page, search, showNotification]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchUsers();
    }, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const atLimit = licData
    ? (licData.users_created ?? 0) >= (licData.user_limit ?? Infinity)
    : false;
  const domainName = licData?.domain_name || "";
  const slotsRemaining = licData
    ? Math.max(
        0,
        (licData.user_limit ?? Infinity) - (licData.users_created ?? 0),
      )
    : Infinity;
  const bulkPreviewCount = bulkIsExcel ? null : bulkPreviewRows.length;
  const bulkExceedsSlots =
    Number.isFinite(slotsRemaining) &&
    bulkPreviewCount != null &&
    bulkPreviewCount > slotsRemaining;

  const resetBulkUpload = () => {
    setBulkFile(null);
    setBulkFileName("");
    setBulkPreviewRows([]);
    setBulkIsExcel(false);
    setBulkDragOver(false);
    setBulkErrors([]);
    if (bulkFileRef.current) bulkFileRef.current.value = "";
  };

  const downloadUserBulkTemplate = () => {
    const sampleDomain = domainName || "company.com";
    const csv = [
      "full_name,email,password,phone,department,access_role",
      `Jane Doe,jane@${sampleDomain},SecurePass123,9876543210,Human Resources,L1`,
      `John Smith,john@${sampleDomain},AnotherPass456,,Finance,L2`,
    ].join("\n");
    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    anchor.download = "users_bulk_template.csv";
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  const handleBulkFile = (file) => {
    if (!file) return;
    if (!isUserSpreadsheetFile(file)) {
      setBulkErrors(["Please upload a .csv or .xlsx file"]);
      return;
    }

    setBulkFile(file);
    setBulkFileName(file.name);
    setBulkErrors([]);

    const isExcel = /\.xlsx?$/.test(file.name.toLowerCase());
    setBulkIsExcel(isExcel);

    if (isExcel) {
      setBulkPreviewRows([]);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const { rows, lineErrors } = parseUserCsvText(
        event.target.result,
        domainName,
      );
      setBulkPreviewRows(rows);
      if (lineErrors.length) setBulkErrors(lineErrors);
    };
    reader.readAsText(file);
  };

  // ── Validate create form ────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Required";
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) {
      e.email = "Valid email required";
    } else if (!domainName) {
      e.email =
        "Tenant domain is not configured. Contact your platform administrator.";
    } else {
      const domainErr = userEmailDomainError(form.email, domainName);
      if (domainErr) e.email = domainErr;
    }
    if (!form.password || form.password.length < 8)
      e.password = "Min. 8 characters";
    if (form.phone && !/^[0-9]{10}$/.test(form.phone)) {
      e.phone = "Enter valid 10-digit number";
    }
    if (!form.department_ids?.length)
      e.department_ids = "Department is required";
    if (!form.access_role) e.access_role = "Role is required";
    if (form.is_spoc && !form.spoc_department_id) {
      e.spoc_department_id = "Select which department they are SPOC of";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Create user ─────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!validate()) return;
    setSaving(true);

    try {
      const res = await _fetch("/tenant/users", {
        method: "POST",
        body: JSON.stringify({
          full_name: form.full_name,
          email: form.email,
          password: form.password,
          phone: form.phone || null,
          department_id: form.department_ids[0],
          department_ids: form.department_ids,
          access_role: selectionIsSpocHead(
            form.is_spoc,
            form.spoc_department_id,
            headDepartmentIdFor(departments),
          )
            ? "L1"
            : form.access_role || "L1",
          is_spoc: !!form.is_spoc,
          ...(form.is_spoc && form.spoc_department_id
            ? { spoc_department_id: form.spoc_department_id }
            : {}),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        showNotification(data.detail || "Failed to create user", "error");
        return;
      }
      showNotification(`User "${data.full_name}" created`);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setErrors({});
      setShowCreatePassword(false);
      fetchUsers();
      const licRes = await _fetch("/tenant/dashboard");
      if (licRes.ok) setLicData(await licRes.json());
    } catch {
      showNotification("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleBulkCreate = async () => {
    if (!bulkFile) {
      setBulkErrors(["Choose a CSV or Excel file to upload."]);
      return;
    }
    if (!bulkIsExcel && bulkErrors.length) return;
    if (!bulkIsExcel && !bulkPreviewRows.length) {
      setBulkErrors(["No valid user rows found in the CSV file."]);
      return;
    }
    if (bulkExceedsSlots) {
      setBulkErrors([
        `Only ${slotsRemaining} user slot${slotsRemaining === 1 ? "" : "s"} remaining — remove ${bulkPreviewRows.length - slotsRemaining} row(s).`,
      ]);
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("file", bulkFile);
      const res = await apiFetch("/tenant/users/bulk/import", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        if (Array.isArray(detail)) {
          setBulkErrors(detail.map(String));
        } else {
          showNotification(detail || "Failed to import users", "error");
        }
        return;
      }
      showNotification(
        `${data.created} user${data.created === 1 ? "" : "s"} imported successfully`,
      );
      setShowBulk(false);
      resetBulkUpload();
      fetchUsers();
      const licRes = await _fetch("/tenant/dashboard");
      if (licRes.ok) setLicData(await licRes.json());
    } catch {
      showNotification("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Delegate assignment ─────────────────────────────────────────────────────
  const openDelegateModal = (user) => {
    setDelegateUser(user);
    setDelegateDates({ start_date: "", end_date: "" });
  };

  const handleAssignDelegate = async () => {
    if (!delegateUser) return;
    if (!delegateDates.start_date || !delegateDates.end_date) {
      showNotification("Please select both start and end dates", "error");
      return;
    }
    if (delegateDates.end_date < delegateDates.start_date) {
      showNotification("End date must be on or after start date", "error");
      return;
    }
    setDelegateSaving(true);
    try {
      const res = await _fetch("/head-department/members", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: delegateUser.id,
          start_date: delegateDates.start_date,
          end_date: delegateDates.end_date,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || "Failed to assign delegate", "error");
        return;
      }
      showNotification(
        `${delegateUser.full_name} has been assigned as Delegate`,
        "success"
      );
      setDelegateUser(null);
      // Refresh user list to reflect new badge
      fetchUsers();
      fetchDelegateMembers();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setDelegateSaving(false);
    }
  };

  // ── Revoke delegate access (admin can revoke any time, before expiry) ───────
  const handleRevokeDelegate = async () => {
    if (!revokeTarget?.member?.id) return;
    setRevoking(true);
    try {
      const res = await _fetch(
        `/head-department/members/${revokeTarget.member.id}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || "Failed to revoke delegate access", "error");
        return;
      }
      showNotification(
        `Delegate access revoked for ${
          revokeTarget.member.user_name || revokeTarget.user?.full_name || "user"
        }`,
        "success"
      );
      setRevokeTarget(null);
      fetchUsers();
      fetchDelegateMembers();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setRevoking(false);
    }
  };

  // ── Edit user ───────────────────────────────────────────────────────────────
  const openEdit = (user) => {
    const headId = headDepartmentIdFor(departments, user);
    const spocHead =
      user.head_member_type === "spoc_head" ||
      selectionIsSpocHead(user.is_spoc, user.spoc_department_id, headId);
    setEditUser(user);
    setEditForm({
      full_name: user.full_name || "",
      email: user.email || "",
      phone: user.phone || "",
      department_ids: userAssignedDepartmentIds(user),
      access_role: spocHead ? "L1" : user.access_role || "L1",
      is_spoc: !!user.is_spoc,
      spoc_department_id: user.spoc_department_id
        ? String(user.spoc_department_id)
        : "",
    });
  };

  const openResetPassword = (user) => {
    setResetUser(user);
    setResetPassword("");
  };

  const handleEdit = async () => {
    if (!editForm.full_name.trim()) {
      showNotification("Name is required", "error");
      return;
    }

    if (!editForm.email || !/\S+@\S+\.\S+/.test(editForm.email)) {
      showNotification("Valid email required", "error");
      return;
    }

    const editDomainErr = userEmailDomainError(editForm.email, domainName);
    if (editDomainErr) {
      showNotification(editDomainErr, "error");
      return;
    }

    if (editForm.phone && !/^[0-9]{10}$/.test(editForm.phone)) {
      showNotification("Enter valid 10-digit number", "error");
      return;
    }
    if (!editForm.department_ids?.length) {
      showNotification("Department is required", "error");
      return;
    }
    if (!editForm.access_role) {
      showNotification("Role is required", "error");
      return;
    }
    if (editForm.is_spoc && !editForm.spoc_department_id) {
      showNotification("Select which department they are SPOC of", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await _fetch(`/tenant/users/${editUser.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          full_name: editForm.full_name,
          email: editForm.email,
          ...(editForm.phone ? { phone: editForm.phone } : { phone: null }),
          department_id: editForm.department_ids[0],
          department_ids: editForm.department_ids,
          access_role: selectionIsSpocHead(
            editForm.is_spoc,
            editForm.spoc_department_id,
            headDepartmentIdFor(departments, editUser),
          )
            ? "L1"
            : editForm.access_role,
          is_spoc: !!editForm.is_spoc,
          ...(editForm.is_spoc && editForm.spoc_department_id
            ? { spoc_department_id: editForm.spoc_department_id }
            : {}),
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        showNotification(d.detail || "Failed to update", "error");
        return;
      }
      showNotification("User updated");
      setEditUser(null);
      fetchUsers();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetUser) return;
    if (!resetPassword || resetPassword.length < 8) {
      showNotification("Password must be at least 8 characters", "error");
      return;
    }
    setResetSaving(true);
    try {
      const res = await _fetch(`/tenant/users/${resetUser.id}/reset-password`, {
        method: "POST",
        body: JSON.stringify({ password: resetPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        showNotification(data.detail || "Failed to reset password", "error");
        return;
      }
      showNotification(
        data.message || `Password reset for ${resetUser.full_name}`,
      );
      setResetUser(null);
      setResetPassword("");
      setShowResetPassword(false);
    } catch {
      showNotification("Network error", "error");
    } finally {
      setResetSaving(false);
    }
  };

  // ── Toggle active / inactive ────────────────────────────────────────────────
  const toggleStatus = async (user) => {
    if (toggling[user.id]) return; // prevent double-click
    const newStatus = user.status === "active" ? "inactive" : "active";
    setToggling((prev) => ({ ...prev, [user.id]: true }));
    try {
      const res = await _fetch(`/tenant/users/${user.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        showNotification(d.detail || "Failed to update status", "error");
        return;
      }
      showNotification(
        `User ${newStatus === "active" ? "activated" : "deactivated"}`,
      );
      fetchUsers();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setToggling((prev) => {
        const n = { ...prev };
        delete n[user.id];
        return n;
      });
    }
  };

  // ── Delete user ─────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await _fetch(`/tenant/users/${deleteUser.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const d = await res.json();
        showNotification(d.detail || "Failed to delete", "error");
        return;
      }
      showNotification(`User "${deleteUser.full_name}" permanently deleted`);
      setDeleteUser(null);
      fetchUsers();
      const licRes = await _fetch("/tenant/dashboard");
      if (licRes.ok) setLicData(await licRes.json());
    } catch {
      showNotification("Network error", "error");
    } finally {
      setDeleting(false);
    }
  };

  // Active / inactive counts
  const activeCount = users.filter((u) => u.status === "active").length;
  const inactiveCount = users.filter((u) => u.status !== "active").length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Header ── */}
      <div
        data-tour-id="tour-users-header"
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
            <Users size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Manage Users</h1>
            <p className="text-sm text-slate-400 flex items-center gap-1 mt-0.5">
              <Package size={11} />
              {total} of {licData?.user_limit ?? "—"} user slots used
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-tour-id="tour-users-bulk"
            variant="secondary"
            leftIcon={<ListPlus size={15} />}
            onClick={() => setShowBulk(true)}
            disabled={atLimit}
            title={atLimit ? "User limit reached. Contact your admin." : ""}
          >
            Bulk Upload
          </Button>
          <Button
            data-tour-id="tour-add-user"
            leftIcon={<UserPlus size={15} />}
            onClick={() => setShowCreate(true)}
            disabled={atLimit}
            title={atLimit ? "User limit reached. Contact your admin." : ""}
          >
            Add User
          </Button>
        </div>
      </div>

      {/* ── Stats row ── */}
      <div
        data-tour-id="tour-users-stats"
        className="grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        {[
          {
            label: "Total Users",
            value: total,
            icon: <Users size={16} />,
            gradient: "bg-gradient-to-br from-blue-400 to-indigo-800",
          },
          {
            label: "Active",
            value: activeCount,
            icon: <UserCheck size={16} />,
            gradient: "bg-gradient-to-br from-emerald-400 to-teal-800",
          },
          {
            label: "Inactive",
            value: inactiveCount,
            icon: <UserX size={16} />,
            gradient: "bg-gradient-to-br from-slate-400 to-slate-800",
          },
          {
            label: "Limit",
            value: licData?.user_limit ?? "—",
            icon: <Package size={16} />,
            gradient: "bg-gradient-to-br from-violet-400 to-purple-800",
          },
        ].map((s, i) => (
          <DashboardStatCard key={i} {...s} loading={loading} />
        ))}
      </div>

      {/* ── At-limit warning ── */}
      {atLimit && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
          <div className="p-1.5 rounded-lg bg-amber-100">
            <AlertTriangle size={14} className="text-amber-600" />
          </div>
          <span>
            You've reached your user limit. Contact your administrator to
            increase the license.
          </span>
        </div>
      )}

      {/* ── Table card ── */}
      <Card padding={false}>
        {/* toolbar */}
        <div
          data-tour-id="tour-users-search"
          className="flex flex-col sm:flex-row items-start sm:items-center gap-3 px-5 py-4 border-b border-slate-100"
        >
          <div className="relative flex-1 max-w-sm">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
            />
          </div>
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="p-2 rounded-xl text-slate-400 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 transition-all disabled:opacity-40"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        <div data-tour-id="tour-users-table">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
              <RefreshCw size={14} className="animate-spin" /> Loading users…
            </div>
          ) : users.length === 0 ? (
            <div className="py-16">
              <EmptyState
                icon={<Users size={44} />}
                title="No users found"
                description={
                  search
                    ? "Try a different search term"
                    : "Add your first user to get started"
                }
                action={
                  !search && (
                    <Button
                      leftIcon={<UserPlus size={14} />}
                      onClick={() => setShowCreate(true)}
                      disabled={atLimit}
                    >
                      Add First User
                    </Button>
                  )
                }
              />
            </div>
          ) : (
            <div className="w-full max-w-full overflow-auto scrollbar-thin max-h-[calc(5*5.5rem)]">
              <div className="divide-y divide-slate-50">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50/60 transition-colors group"
                  >
                    {/* Left — avatar + info */}
                    <div className="flex items-center gap-3">
                      <Avatar name={user.full_name || user.email} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-800">
                            {user.full_name}
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
                        ${
                          user.status === "active"
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-slate-100 text-slate-500"
                        }`}
                          >
                            {user.status === "active" ? (
                              <>
                                <CheckCircle2 size={10} /> Active
                              </>
                            ) : (
                              <>
                                <XCircle size={10} /> Inactive
                              </>
                            )}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                          <Mail size={10} />
                          {user.email}
                        </p>
                        {user.domain_user_id && (
                          <p className="text-[11px] text-slate-400 font-mono mt-0.5 truncate max-w-xs">
                            ID: {user.domain_user_id}
                          </p>
                        )}
                        <p className="text-xs text-slate-400 flex items-center gap-2 mt-0.5 flex-wrap">
                          {formatUserDepartments(user, departments) && (
                            <span className="inline-flex items-center gap-1">
                              <Building2 size={10} />
                              {formatUserDepartments(user, departments)}
                            </span>
                          )}
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-600 text-[10px] font-semibold">
                            {user.access_role || "L1"}
                          </span>
                          {user.is_spoc &&
                            user.head_member_type !== "spoc_head" && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-semibold">
                                SPOC
                              </span>
                            )}
                          {user.head_member_type === "spoc_head" && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-800 text-[10px] font-semibold">
                              SPOC Head
                            </span>
                          )}
                          {user.head_member_type === "delegate" &&
                            user.head_access && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-violet-50 text-violet-700 text-[10px] font-semibold">
                                Delegate
                              </span>
                            )}
                        </p>
                        {user.created_at && (
                          <p className="text-xs text-slate-300 flex items-center gap-1 mt-0.5">
                            <Clock size={10} />
                            Joined{" "}
                            {new Date(user.created_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Right — action buttons */}
                    {(() => {
                      const isToggling = !!toggling[user.id];
                      const isBusy = isToggling || saving || deleting;
                      return (
                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-12 sm:ml-0">
                          {/* Edit */}
                          <button
                            onClick={() => openEdit(user)}
                            disabled={isBusy}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Edit user"
                          >
                            <Edit2 size={12} /> Edit
                          </button>

                          {/* Activity Log — visible for every user */}
                          <button
                            onClick={() => setActivityUser(user)}
                            disabled={isBusy}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title="View activity log"
                          >
                            <ScrollText size={12} /> Activity
                          </button>

                          {/* Delegate / Revoke — only one delegate at a time */}
                          {(() => {
                            const canManage =
                              currentUser?.can_manage_head_delegates ||
                              currentUser?.role === "tenant_admin";
                            if (!canManage) return null;

                            const isCurrentDelegate =
                              currentDelegate &&
                              String(currentDelegate.user_id) === String(user.id);

                            // The holder gets a Revoke button — available any
                            // time, independent of the expiry date.
                            if (isCurrentDelegate) {
                              return (
                                <button
                                  onClick={() =>
                                    setRevokeTarget({
                                      member: currentDelegate,
                                      user,
                                    })
                                  }
                                  disabled={isBusy || revoking}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  title={`Revoke Delegate access (expires ${
                                    currentDelegate.end_date || "—"
                                  })`}
                                >
                                  <UserX size={12} /> Revoke Delegate
                                </button>
                              );
                            }

                            const eligible =
                              user.is_spoc &&
                              user.head_member_type !== "spoc_head" &&
                              user.status === "active";
                            if (!eligible) return null;

                            // Someone else already holds the single slot
                            const blocked = !!currentDelegate;
                            return (
                              <button
                                onClick={() => openDelegateModal(user)}
                                disabled={isBusy || blocked}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                title={
                                  blocked
                                    ? `${
                                        currentDelegate.user_name || "Another user"
                                      } is currently the Delegate — revoke that access first`
                                    : "Assign as Delegate (temporary global access)"
                                }
                              >
                                <UserCheck size={12} /> Delegate
                              </button>
                            );
                          })()}

                          <button
                            onClick={() => openResetPassword(user)}
                            disabled={isBusy}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Reset password"
                          >
                            <Key size={12} /> Reset Password
                          </button>

                          {/* Activate / Deactivate */}
                          <button
                            onClick={() => toggleStatus(user)}
                            disabled={isBusy}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed
                          ${
                            user.status === "active"
                              ? "text-amber-600 bg-amber-50 hover:bg-amber-100"
                              : "text-emerald-600 bg-emerald-50 hover:bg-emerald-100"
                          }`}
                            title={
                              user.status === "active"
                                ? "Deactivate user"
                                : "Activate user"
                            }
                          >
                            {isToggling ? (
                              <RefreshCw size={12} className="animate-spin" />
                            ) : user.status === "active" ? (
                              <ToggleLeft size={12} />
                            ) : (
                              <ToggleRight size={12} />
                            )}
                            {isToggling
                              ? "Updating…"
                              : user.status === "active"
                                ? "Deactivate"
                                : "Activate"}
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() => setDeleteUser(user)}
                            disabled={isBusy}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-500 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Delete user"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          {users.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100">
              <Pagination
                page={page}
                pages={pages}
                total={total}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
                itemLabel="users"
              />
              <div className="flex items-center justify-end gap-3 text-xs text-slate-400 mt-2">
                <span className="flex items-center gap-1">
                  <CheckCircle2 size={10} className="text-emerald-500" />
                  {activeCount} active
                </span>
                <span className="flex items-center gap-1">
                  <XCircle size={10} className="text-slate-400" />
                  {inactiveCount} inactive
                </span>
              </div>
            </div>
          )}
        </div>
      </Card>
      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setErrors({});
          setForm(EMPTY_FORM);
          setShowCreatePassword(false);
        }}
        title="Create New User"
      >
        <div className="space-y-4">
          <FormErrorsSummary errors={errors} />
          <Input
            label="Full Name"
            placeholder="Jane Doe"
            value={form.full_name}
            onChange={(e) =>
              setForm((f) => ({ ...f, full_name: e.target.value }))
            }
            error={errors.full_name}
            leftElement={<Users size={14} />}
          />
          <Input
            label="Email Address"
            type="email"
            placeholder={domainName ? `jane@${domainName}` : "jane@company.com"}
            value={form.email}
            onChange={(e) => {
              const email = e.target.value;
              setForm((f) => ({ ...f, email }));
              const domainErr = userEmailDomainError(email, domainName);
              setErrors((prev) => {
                const next = { ...prev };
                if (domainErr) next.email = domainErr;
                else if (next.email?.includes("tenant domain"))
                  delete next.email;
                return next;
              });
            }}
            onBlur={() => {
              const domainErr = userEmailDomainError(form.email, domainName);
              if (domainErr) {
                setErrors((prev) => ({ ...prev, email: domainErr }));
              }
            }}
            error={errors.email}
            hint={domainName ? `Must use @${domainName}` : undefined}
            leftElement={<Mail size={14} />}
          />
          {emailMatchesTenantDomain(form.email, domainName) &&
            previewDomainUserId(domainName, form.email) && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  Domain User ID
                </p>
                <p className="text-xs font-mono text-slate-700 mt-1 break-all">
                  {previewDomainUserId(domainName, form.email)}
                  <span className="text-slate-400">:(uuid on save)</span>
                </p>
              </div>
            )}
          <Input
            label="Password"
            type={showCreatePassword ? "text" : "password"}
            placeholder="Min. 8 characters"
            value={form.password}
            onChange={(e) =>
              setForm((f) => ({ ...f, password: e.target.value }))
            }
            error={errors.password}
            leftElement={<Key size={14} />}
            rightElement={
              <button
                type="button"
                onClick={() => setShowCreatePassword((v) => !v)}
                className="text-slate-400 hover:text-slate-600"
                aria-label={
                  showCreatePassword ? "Hide password" : "Show password"
                }
                title={showCreatePassword ? "Hide password" : "Show password"}
              >
                {showCreatePassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            }
          />
          <Input
            label="Contact Number"
            placeholder="e.g. 9876543210"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            error={errors.phone}
            leftElement={<Phone size={14} />}
          />
          <DepartmentMultiSelect
            departments={departments}
            value={form.department_ids}
            onChange={(ids) =>
              setForm((f) => {
                const next = { ...f, department_ids: ids };
                if (
                  f.spoc_department_id &&
                  !ids.map(String).includes(String(f.spoc_department_id))
                ) {
                  next.spoc_department_id = "";
                }
                return next;
              })
            }
            error={errors.department_ids}
          />
          <SpocFields
            departments={departments}
            departmentIds={form.department_ids}
            isSpoc={form.is_spoc}
            spocDepartmentId={form.spoc_department_id}
            headDepartmentId={departments.find((d) => d.is_head)?.id}
            onIsSpocChange={(checked) =>
              setForm((f) =>
                applySpocHeadRole(
                  { ...f, is_spoc: checked },
                  headDepartmentIdFor(departments),
                ),
              )
            }
            onSpocDepartmentChange={(id) =>
              setForm((f) =>
                applySpocHeadRole(
                  { ...f, spoc_department_id: id },
                  headDepartmentIdFor(departments),
                ),
              )
            }
          />
          {errors.spoc_department_id && (
            <p className="text-xs text-red-600 -mt-1">
              {errors.spoc_department_id}
            </p>
          )}
          <Select
            label="Role"
            value={
              selectionIsSpocHead(
                form.is_spoc,
                form.spoc_department_id,
                headDepartmentIdFor(departments),
              )
                ? "L1"
                : form.access_role
            }
            disabled={selectionIsSpocHead(
              form.is_spoc,
              form.spoc_department_id,
              headDepartmentIdFor(departments),
            )}
            onChange={(e) =>
              setForm((f) => ({ ...f, access_role: e.target.value }))
            }
            error={errors.access_role}
          >
            {accessRoles.map((role) => (
              <option key={role.key} value={role.key}>
                {role.label || role.key}
                {role.grants_all_licensed ? " — all licensed modules" : ""}
              </option>
            ))}
          </Select>
          {departments.length === 0 && (
            <p className="text-xs text-amber-700 -mt-2">
              Create a department under Tenant Admins → Departments before
              adding users.
            </p>
          )}
          <p className="text-xs leading-relaxed text-slate-500">
            {selectionIsSpocHead(
              form.is_spoc,
              form.spoc_department_id,
              headDepartmentIdFor(departments),
            )
              ? "SPOC Head is always L1 and receives every module in the organisation license. Choose a different SPOC department to assign another role."
              : "L1 users automatically receive every module in the organisation license. L2–L5 access is configured in Settings → Module Access."}
          </p>
        </div>
        <div className="flex gap-3 mt-6">
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreate(false);
              setErrors({});
              setForm(EMPTY_FORM);
              setShowCreatePassword(false);
            }}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            loading={saving}
            leftIcon={<UserPlus size={14} />}
            className="flex-1"
          >
            Create User
          </Button>
        </div>
      </Modal>

      <Modal
        open={showBulk}
        onClose={() => {
          setShowBulk(false);
          resetBulkUpload();
        }}
        title="Bulk Upload Users"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Upload a CSV or Excel file with one user per row.
            {domainName ? (
              <>
                {" "}
                Emails must use <strong>@{domainName}</strong>.
              </>
            ) : null}
          </p>
          {Number.isFinite(slotsRemaining) && (
            <p className="text-xs text-slate-500">
              {slotsRemaining} user slot{slotsRemaining === 1 ? "" : "s"}{" "}
              remaining
            </p>
          )}

          <button
            type="button"
            onClick={downloadUserBulkTemplate}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-slate-600 hover:text-blue-600 transition-all group"
          >
            <div className="p-2 rounded-lg bg-slate-100 group-hover:bg-blue-100 transition-all">
              <Download
                size={14}
                className="text-slate-500 group-hover:text-blue-500"
              />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold">Download CSV Template</p>
              <p className="text-xs text-slate-400">
                Columns: full_name, email, password, phone (optional),
                department (optional; comma-separated for multiple), access_role
                (optional, default L1). Open in Excel or Google Sheets.
              </p>
            </div>
            <ChevronRight
              size={14}
              className="ml-auto text-slate-300 group-hover:text-blue-400"
            />
          </button>

          <div
            onDragOver={(event) => {
              event.preventDefault();
              setBulkDragOver(true);
            }}
            onDragLeave={() => setBulkDragOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setBulkDragOver(false);
              handleBulkFile(event.dataTransfer.files[0]);
            }}
            onClick={() => bulkFileRef.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed flex flex-col items-center justify-center py-8 gap-2.5 transition-all ${
              bulkDragOver
                ? "border-blue-400 bg-blue-50"
                : "border-slate-200 hover:border-slate-400"
            }`}
          >
            <div
              className={`p-3 rounded-xl ${bulkDragOver ? "bg-blue-100" : "bg-slate-100"}`}
            >
              {bulkIsExcel ? (
                <FileSpreadsheet
                  size={20}
                  className={bulkDragOver ? "text-blue-500" : "text-slate-400"}
                />
              ) : (
                <Upload
                  size={20}
                  className={bulkDragOver ? "text-blue-500" : "text-slate-400"}
                />
              )}
            </div>
            <div className="text-center px-4">
              <p className="text-sm font-semibold text-slate-700">
                {bulkFileName || "Drop CSV or Excel file here"}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {bulkFileName
                  ? bulkIsExcel
                    ? "Excel file selected — row count verified on upload"
                    : `${bulkPreviewRows.length} user${bulkPreviewRows.length === 1 ? "" : "s"} ready to import`
                  : "or click to browse (.csv, .xlsx)"}
              </p>
            </div>
            <input
              ref={bulkFileRef}
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(event) => handleBulkFile(event.target.files[0])}
            />
          </div>

          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2.5">
              Expected columns
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-3">
              {[
                ["full_name", "User's full name (required)"],
                ["email", "Must match tenant domain (required)"],
                ["password", "Min. 8 characters (required)"],
                ["phone", "10-digit number (optional)"],
              ].map(([col, desc]) => (
                <div key={col} className="flex items-start gap-1.5">
                  <code className="text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded flex-shrink-0">
                    {col}
                  </code>
                  <span className="text-[11px] text-slate-500 mt-0.5">
                    {desc}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {bulkErrors.length > 0 && (
            <ul className="text-xs text-red-600 space-y-1 max-h-32 overflow-y-auto">
              {bulkErrors.map((err, i) => (
                <li key={`${err}-${i}`}>{err}</li>
              ))}
            </ul>
          )}
          {bulkPreviewRows.length > 0 &&
            bulkErrors.length === 0 &&
            !bulkExceedsSlots && (
              <p className="text-xs text-teal-700 font-medium">
                Ready to import {bulkPreviewRows.length} user
                {bulkPreviewRows.length === 1 ? "" : "s"}
              </p>
            )}
          {bulkIsExcel && bulkFileName && bulkErrors.length === 0 && (
            <p className="text-xs text-teal-700 font-medium">
              Excel file ready — click Import to create users
            </p>
          )}
          {bulkExceedsSlots && bulkErrors.length === 0 && (
            <p className="text-xs text-amber-700 font-medium">
              {bulkPreviewRows.length} users in file but only {slotsRemaining}{" "}
              slot
              {slotsRemaining === 1 ? "" : "s"} left — remove{" "}
              {bulkPreviewRows.length - slotsRemaining} row(s).
            </p>
          )}
        </div>
        <div className="flex gap-3 mt-6">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => {
              setShowBulk(false);
              resetBulkUpload();
            }}
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={handleBulkCreate}
            loading={saving}
            disabled={
              !bulkFile ||
              bulkExceedsSlots ||
              (!bulkIsExcel && !!bulkErrors.length)
            }
            leftIcon={<ListPlus size={14} />}
          >
            Import Users
          </Button>
        </div>
      </Modal>

      {/* ══ Edit User Modal ══ */}
      <Modal
        open={!!editUser}
        onClose={() => setEditUser(null)}
        title="Edit User"
        size="sm"
      >
        {editUser && (
          <>
            <FormErrorsSummary
              errors={{
                email:
                  userEmailDomainError(editForm.email, domainName) || undefined,
              }}
            />
            <div className="flex items-center gap-3 mb-5 p-3 bg-slate-50 rounded-xl">
              <Avatar name={editUser.full_name} size="lg" />
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {editUser.full_name}
                </p>
                <p className="text-xs text-slate-500">{editUser.email}</p>
              </div>
            </div>
            <Input
              label="Full Name"
              value={editForm.full_name}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, full_name: e.target.value }))
              }
              leftElement={<Users size={14} />}
            />

            <Input
              label="Email Address"
              type="email"
              placeholder={domainName ? `user@${domainName}` : undefined}
              value={editForm.email}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, email: e.target.value }))
              }
              error={
                userEmailDomainError(editForm.email, domainName) || undefined
              }
              hint={domainName ? `Must use @${domainName}` : undefined}
              leftElement={<Mail size={14} />}
            />
            {emailMatchesTenantDomain(editForm.email, domainName) &&
              (editUser.domain_user_id ||
                previewDomainUserId(domainName, editForm.email)) && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                    Domain User ID
                  </p>
                  <p className="text-xs font-mono text-slate-700 mt-1 break-all">
                    {editForm.email !== editUser.email
                      ? `${previewDomainUserId(domainName, editForm.email)}:(updated on save)`
                      : editUser.domain_user_id}
                  </p>
                </div>
              )}

            <Input
              label="Contact Number"
              value={editForm.phone}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, phone: e.target.value }))
              }
              leftElement={<Phone size={14} />}
            />
            <DepartmentMultiSelect
              departments={departments}
              value={editForm.department_ids}
              onChange={(ids) =>
                setEditForm((f) => {
                  const next = { ...f, department_ids: ids };
                  if (
                    f.spoc_department_id &&
                    !ids.map(String).includes(String(f.spoc_department_id))
                  ) {
                    next.spoc_department_id = "";
                  }
                  return next;
                })
              }
            />

            <SpocFields
              departments={departments}
              departmentIds={editForm.department_ids}
              isSpoc={editForm.is_spoc}
              spocDepartmentId={editForm.spoc_department_id}
              headDepartmentId={
                departments.find((d) => d.is_head)?.id ||
                editUser.head_department_id
              }
              onIsSpocChange={(checked) =>
                setEditForm((f) =>
                  applySpocHeadRole(
                    { ...f, is_spoc: checked },
                    headDepartmentIdFor(departments, editUser),
                  ),
                )
              }
              onSpocDepartmentChange={(id) =>
                setEditForm((f) =>
                  applySpocHeadRole(
                    { ...f, spoc_department_id: id },
                    headDepartmentIdFor(departments, editUser),
                  ),
                )
              }
            />

            {(editUser.head_member_type === "spoc_head" ||
              (editForm.is_spoc &&
                editForm.spoc_department_id &&
                String(editForm.spoc_department_id) ===
                  String(
                    departments.find((d) => d.is_head)?.id ||
                      editUser.head_department_id ||
                      "",
                  ))) &&
              (currentUser?.can_manage_head_delegates ||
                currentUser?.role === "tenant_admin") && (
                <DelegateAccessSection canManage />
              )}

            <Select
              label="Role"
              value={
                selectionIsSpocHead(
                  editForm.is_spoc,
                  editForm.spoc_department_id,
                  headDepartmentIdFor(departments, editUser),
                )
                  ? "L1"
                  : editForm.access_role
              }
              disabled={selectionIsSpocHead(
                editForm.is_spoc,
                editForm.spoc_department_id,
                headDepartmentIdFor(departments, editUser),
              )}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, access_role: e.target.value }))
              }
            >
              {accessRoles.map((role) => (
                <option key={role.key} value={role.key}>
                  {role.label || role.key}
                  {role.grants_all_licensed ? " — all licensed modules" : ""}
                </option>
              ))}
            </Select>
            {selectionIsSpocHead(
              editForm.is_spoc,
              editForm.spoc_department_id,
              headDepartmentIdFor(departments, editUser),
            ) && (
              <p className="mt-2 text-xs leading-relaxed text-slate-500">
                SPOC Head is always L1 and receives every module in the
                organisation license. Choose a different SPOC department to
                assign another role.
              </p>
            )}
            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => setEditUser(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEdit}
                loading={saving}
                leftIcon={<Edit2 size={14} />}
                className="flex-1"
              >
                Save Changes
              </Button>
            </div>
          </>
        )}
      </Modal>

      <Modal
        open={!!resetUser}
        onClose={() => {
          setResetUser(null);
          setResetPassword("");
          setShowResetPassword(false);
        }}
        title="Reset Password"
        size="sm"
      >
        {resetUser && (
          <>
            <div className="flex items-center gap-3 mb-5 p-3 bg-violet-50 rounded-xl border border-violet-100">
              <Avatar name={resetUser.full_name} size="lg" />
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {resetUser.full_name}
                </p>
                <p className="text-xs text-slate-500">{resetUser.email}</p>
              </div>
            </div>
            <Input
              label="New Password"
              type={showResetPassword ? "text" : "password"}
              placeholder="Minimum 8 characters"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              leftElement={<Key size={14} />}
              rightElement={
                <button
                  type="button"
                  onClick={() => setShowResetPassword((v) => !v)}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label={
                    showResetPassword ? "Hide password" : "Show password"
                  }
                  title={showResetPassword ? "Hide password" : "Show password"}
                >
                  {showResetPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              }
            />
            <p className="text-xs text-slate-500 mt-2">
              The user will receive a separate password reset email with the new
              credentials.
            </p>
            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setResetUser(null);
                  setResetPassword("");
                  setShowResetPassword(false);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                loading={resetSaving}
                leftIcon={<Key size={14} />}
                className="flex-1"
              >
                Reset Password
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* ══ Delete Confirm Modal ══ */}
      <Modal
        open={!!deleteUser}
        onClose={() => setDeleteUser(null)}
        title="Delete User"
        size="sm"
      >
        {deleteUser && (
          <>
            <div className="flex flex-col items-center text-center gap-3 py-2">
              <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
                <Trash2 size={24} className="text-red-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  Delete "{deleteUser.full_name}"?
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  This permanently deletes the user and their submissions,
                  drafts, and attachments. Use Deactivate to block login without
                  removing data.
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => setDeleteUser(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                loading={deleting}
                leftIcon={<Trash2 size={14} />}
                className="flex-1"
              >
                Delete User
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* ── Assign Delegate Modal ─────────────────────────────────────── */}
      <Modal
        open={!!delegateUser}
        onClose={() => !delegateSaving && setDelegateUser(null)}
        title="Assign Delegate Access"
      >
        {delegateUser && (
          <>
            {/* User info banner */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-100 mb-5">
              <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {(delegateUser.full_name || "?")[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {delegateUser.full_name}
                </p>
                <p className="text-xs text-slate-500 truncate">{delegateUser.email}</p>
              </div>
              <span className="ml-auto flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px] font-semibold">
                SPOC
              </span>
            </div>

            {/* Explanation */}
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              Granting delegate access gives{" "}
              <span className="font-semibold text-slate-700">
                {delegateUser.full_name}
              </span>{" "}
              temporary <span className="font-semibold text-emerald-700">global access</span> across all
              departments for the selected period. Access expires automatically on the
              end date, and can be revoked earlier at any time. Only one delegate can
              hold global access at a time.
            </p>

            {/* Date fields */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={delegateDates.start_date}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) =>
                    setDelegateDates((d) => ({ ...d, start_date: e.target.value }))
                  }
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  End Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={delegateDates.end_date}
                  min={delegateDates.start_date || new Date().toISOString().split("T")[0]}
                  onChange={(e) =>
                    setDelegateDates((d) => ({ ...d, end_date: e.target.value }))
                  }
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 bg-white"
                />
              </div>
            </div>

            {/* Duration hint */}
            {delegateDates.start_date && delegateDates.end_date && delegateDates.end_date >= delegateDates.start_date && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-4 flex items-center gap-1.5">
                <Calendar size={12} />
                Delegate access from{" "}
                <span className="font-semibold">{new Date(delegateDates.start_date + "T00:00:00").toLocaleDateString()}</span>
                {" "}to{" "}
                <span className="font-semibold">{new Date(delegateDates.end_date + "T00:00:00").toLocaleDateString()}</span>
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setDelegateUser(null)}
                disabled={delegateSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignDelegate}
                loading={delegateSaving}
                disabled={!delegateDates.start_date || !delegateDates.end_date || delegateSaving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <UserCheck size={14} />
                Assign Delegate
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* ── Revoke Delegate Modal ────────────────────────────────────── */}
      <Modal
        open={!!revokeTarget}
        onClose={() => !revoking && setRevokeTarget(null)}
        title="Revoke Delegate Access"
      >
        {revokeTarget && (
          <>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-50 border border-rose-100 mb-5">
              <AlertTriangle size={18} className="text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">
                  Remove global access from{" "}
                  {revokeTarget.member.user_name ||
                    revokeTarget.user?.full_name ||
                    "this user"}
                  ?
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  Their access across all departments ends immediately, before the
                  scheduled expiry. They keep their own department and SPOC role.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6 text-xs">
              <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                <p className="text-slate-500 mb-0.5">Granted period</p>
                <p className="font-semibold text-slate-800">
                  {revokeTarget.member.start_date || "—"} →{" "}
                  {revokeTarget.member.end_date || "—"}
                </p>
              </div>
              <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                <p className="text-slate-500 mb-0.5">Assigned by</p>
                <p className="font-semibold text-slate-800 truncate">
                  {revokeTarget.member.assigned_by_name || "—"}
                </p>
              </div>
            </div>

            <p className="text-xs text-slate-500 mb-5">
              Once revoked, another SPOC can be assigned as Delegate. This action is
              recorded in the Head &amp; Delegate activity log.
            </p>

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setRevokeTarget(null)}
                disabled={revoking}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleRevokeDelegate}
                loading={revoking}
                disabled={revoking}
              >
                <UserX size={14} />
                Revoke Access
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* ── User Activity Log Modal ──────────────────────────────────── */}
      <UserActivityLogModal user={activityUser} onClose={() => setActivityUser(null)} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TenantLicensePage
// ═══════════════════════════════════════════════════════════════════════════════
export function TenantLicensePage() {
  const { showNotification } = useApp();
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await _fetch("/tenant/license");
        if (res.ok) setLicense(await res.json());
        else showNotification("Failed to load license", "error");
      } catch {
        showNotification("Network error", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [showNotification]);

  const used = license?.users_created ?? 0;
  const limit = license?.user_limit ?? 1;
  const pct = Math.round((used / limit) * 100);

  const statCards = [
    {
      label: "Plan",
      value: license?.plan ?? "—",
      icon: <Zap size={16} />,
      gradient: "bg-gradient-to-br from-violet-400 to-purple-800",
    },
    {
      label: "Users Created",
      value: `${used}/${limit}`,
      icon: <Users size={16} />,
      gradient: "bg-gradient-to-br from-blue-400 to-indigo-800",
    },
    {
      label: "Utilization",
      value: `${pct}%`,
      icon: <Activity size={16} />,
      gradient:
        pct > 80
          ? "bg-gradient-to-br from-red-400 to-red-800"
          : "bg-gradient-to-br from-emerald-500 to-teal-600",
    },
    {
      label: "Slots Left",
      value: license?.slots_remaining ?? limit - used,
      icon: <TrendingUp size={16} />,
      gradient: "bg-gradient-to-br from-sky-400 to-blue-800",
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div
        data-tour-id="tour-tenant-license-header"
        className="flex items-center gap-3"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-md">
          <Package size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            License &amp; Usage
          </h1>
          <p className="text-sm text-slate-400 flex items-center gap-1 mt-0.5">
            <Shield size={11} /> Subscription details and usage tracking
          </p>
        </div>
      </div>

      {/* Stats row */}
      <div
        data-tour-id="tour-tenant-license-stats"
        className="grid grid-cols-2 lg:grid-cols-4 gap-3"
      >
        {statCards.map((s, i) => (
          <DashboardStatCard key={i} {...s} loading={loading} />
        ))}
      </div>

      {/* Detail card */}
      <div data-tour-id="tour-tenant-license-detail">
        <Card>
          <div className="flex items-center gap-2 mb-5">
            <div className="p-1.5 rounded-lg bg-violet-50">
              <Database size={15} className="text-violet-600" />
            </div>
            <h2 className="text-base font-semibold text-slate-800">
              License Details
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {[
              {
                k: "Plan",
                v: license?.plan,
                icon: <Zap size={13} />,
                color: "text-violet-600 bg-violet-50",
              },
              {
                k: "User Limit",
                v: limit,
                icon: <Users size={13} />,
                color: "text-blue-600 bg-blue-50",
              },
              {
                k: "Users Created",
                v: used,
                icon: <UserCheck size={13} />,
                color: "text-emerald-600 bg-emerald-50",
              },
              {
                k: "Slots Left",
                v: license?.slots_remaining ?? limit - used,
                icon: <TrendingUp size={13} />,
                color: "text-sky-600 bg-sky-50",
              },
              {
                k: "Expires",
                v: license?.expires_at,
                icon: <Calendar size={13} />,
                color: "text-amber-600 bg-amber-50",
              },
              {
                k: "Status",
                v: license?.is_active ? "Active" : "Inactive",
                icon: <Shield size={13} />,
                color: license?.is_active
                  ? "text-emerald-600 bg-emerald-50"
                  : "text-slate-600 bg-slate-100",
              },
            ].map(({ k, v, icon, color }) => (
              <div
                key={k}
                className="p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors"
              >
                <div
                  className={`inline-flex items-center gap-1.5 text-xs font-medium mb-2 px-2 py-0.5 rounded-full ${color}`}
                >
                  {icon}
                  {k}
                </div>
                <p className="font-bold text-slate-800 text-sm">
                  {String(v ?? "—")}
                </p>
              </div>
            ))}
          </div>

          {/* Usage bar */}
          <div className="p-4 rounded-xl border border-slate-100 bg-slate-50">
            <div className="flex justify-between text-sm mb-3">
              <span className="font-medium text-slate-700 flex items-center gap-1.5">
                <Activity size={13} className="text-blue-500" /> License Usage
              </span>
              <span
                className={`font-bold text-sm ${pct > 90 ? "text-red-600" : pct > 70 ? "text-amber-600" : "text-emerald-600"}`}
              >
                {pct}%
              </span>
            </div>
            <ProgressBar
              value={used}
              max={limit}
              color={pct > 90 ? "red" : pct > 70 ? "amber" : "primary"}
            />
            <div className="flex justify-between mt-2 text-xs text-slate-400">
              <span>{used} used</span>
              <span>{limit - used} remaining</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ModuleAccessPanel — Tenant Admin configures Department + Role → modules
// ═══════════════════════════════════════════════════════════════════════════════
function ModuleAccessPanel() {
  const { showNotification } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [licensed, setLicensed] = useState([]);
  const [labels, setLabels] = useState({});
  const [departments, setDepartments] = useState([]);
  const [grants, setGrants] = useState({});
  const [selectedDept, setSelectedDept] = useState("");
  const [newRoleKey, setNewRoleKey] = useState("");
  const [newRoleLabel, setNewRoleLabel] = useState("");

  const grantKey = (deptId, roleKey) => `${deptId}::${roleKey}`;

  const loadAccess = useCallback(async () => {
    setLoading(true);
    try {
      const res = await _fetch("/tenant/settings/module-access");
      const data = await res.json();
      if (!res.ok) {
        showNotification(
          data.detail || "Failed to load module access",
          "error",
        );
        return;
      }
      setRoles(data.roles || []);
      setLicensed(data.licensed_modules || []);
      setLabels(data.module_labels || {});
      const depts = (data.departments || []).filter((d) => d.is_active);
      setDepartments(depts);
      const next = {};
      (data.grants || []).forEach((g) => {
        next[grantKey(g.department_id, g.role_key)] = g.modules || [];
      });
      setGrants(next);
      setSelectedDept((prev) => prev || depts[0]?.id || "");
    } catch {
      showNotification("Failed to load module access", "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const configurableRoles = roles.filter((r) => !r.grants_all_licensed);
  const allLicensedRoles = roles.filter((r) => r.grants_all_licensed);

  const toggleModule = (deptId, roleKey, moduleKey) => {
    const key = grantKey(deptId, roleKey);
    setGrants((prev) => {
      const current = new Set(prev[key] || []);
      if (current.has(moduleKey)) current.delete(moduleKey);
      else current.add(moduleKey);
      return { ...prev, [key]: Array.from(current) };
    });
  };

  const addRole = () => {
    const key = newRoleKey.trim();
    const label = newRoleLabel.trim() || key;
    if (!key) {
      showNotification("Role key is required", "error");
      return;
    }
    if (roles.some((r) => r.key.toLowerCase() === key.toLowerCase())) {
      showNotification(`Role "${key}" already exists`, "error");
      return;
    }
    setRoles((prev) => [...prev, { key, label, grants_all_licensed: false }]);
    setNewRoleKey("");
    setNewRoleLabel("");
  };

  const removeRole = (roleKey) => {
    const role = roles.find((r) => r.key === roleKey);
    if (role?.grants_all_licensed) {
      showNotification("The all-access role cannot be removed", "error");
      return;
    }
    setRoles((prev) => prev.filter((r) => r.key !== roleKey));
    setGrants((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.endsWith(`::${roleKey}`)) delete next[k];
      });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payloadGrants = [];
      departments.forEach((dept) => {
        configurableRoles.forEach((role) => {
          payloadGrants.push({
            department_id: dept.id,
            role_key: role.key,
            modules: grants[grantKey(dept.id, role.key)] || [],
          });
        });
      });
      const res = await _fetch("/tenant/settings/module-access", {
        method: "PUT",
        body: JSON.stringify({ roles, grants: payloadGrants }),
      });
      const data = await res.json();
      if (!res.ok) {
        showNotification(
          data.detail || "Failed to save module access",
          "error",
        );
        return;
      }
      showNotification("Module access saved");
      await loadAccess();
    } catch {
      showNotification("Failed to save module access", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CollapsibleSettingsCard
      tourId="tour-tenant-settings-module-access"
      title="Permission / Module Access"
      description="Assign licensed modules to each department and role. L1 always receives every licensed module."
      icon={<Shield size={18} className="text-white" />}
      iconClassName="bg-gradient-to-br from-indigo-500 to-violet-600"
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-6">
          <RefreshCw size={14} className="animate-spin" /> Loading access
          matrix…
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Organisation license
            </p>
            {licensed.length ? (
              <div className="flex flex-wrap gap-1.5">
                {licensed.map((key) => (
                  <span
                    key={key}
                    className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100"
                  >
                    {labels[key] || key}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                No modules are licensed for this organisation. Ask the platform
                administrator to enable modules first.
              </p>
            )}
          </div>

          {allLicensedRoles.map((role) => (
            <div
              key={role.key}
              className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2.5 text-sm text-indigo-800"
            >
              <span className="font-semibold">{role.label || role.key}</span>{" "}
              users automatically get every licensed module. No per-department
              mapping is required.
            </div>
          ))}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Roles
            </p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {roles.map((role) => (
                <span
                  key={role.key}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700"
                >
                  {role.label || role.key}
                  {!role.grants_all_licensed && (
                    <button
                      type="button"
                      onClick={() => removeRole(role.key)}
                      className="text-slate-400 hover:text-red-500"
                      title={`Remove ${role.key}`}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="Role key (e.g. L6)"
                value={newRoleKey}
                onChange={(e) => setNewRoleKey(e.target.value)}
              />
              <Input
                placeholder="Label (optional)"
                value={newRoleLabel}
                onChange={(e) => setNewRoleLabel(e.target.value)}
              />
              <Button
                variant="secondary"
                leftIcon={<Plus size={14} />}
                onClick={addRole}
              >
                Add role
              </Button>
            </div>
          </div>

          {!departments.length ? (
            <p className="text-sm text-slate-500">
              Create a department first, then assign module access for L2–L5
              (and any custom roles).
            </p>
          ) : (
            <>
              <Select
                label="Department"
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
              >
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.code} — {d.name}
                  </option>
                ))}
              </Select>

              {configurableRoles.length === 0 ? (
                <p className="text-sm text-slate-500">
                  Add a role that does not grant all licensed modules to
                  configure department access.
                </p>
              ) : (
                <div className="space-y-4">
                  {configurableRoles.map((role) => {
                    const assigned = new Set(
                      grants[grantKey(selectedDept, role.key)] || [],
                    );
                    return (
                      <div
                        key={role.key}
                        className="rounded-xl border border-slate-200 p-3"
                      >
                        <p className="text-sm font-semibold text-slate-800 mb-2">
                          {role.label || role.key}
                        </p>
                        {licensed.length === 0 ? (
                          <p className="text-xs text-slate-400">
                            No licensed modules to assign.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {licensed.map((mod) => {
                              const checked = assigned.has(mod);
                              return (
                                <label
                                  key={mod}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer border transition-colors ${
                                    checked
                                      ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    className="rounded border-slate-300"
                                    checked={checked}
                                    onChange={() =>
                                      toggleModule(selectedDept, role.key, mod)
                                    }
                                  />
                                  {labels[mod] || mod}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              loading={saving}
              leftIcon={<Shield size={14} />}
            >
              Save module access
            </Button>
          </div>
        </div>
      )}
    </CollapsibleSettingsCard>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TenantSettingsPage
// ═══════════════════════════════════════════════════════════════════════════════
function LdapSettingsCard() {
  return (
    <CollapsibleSettingsCard
      tourId="tour-tenant-settings-ldap"
      title="LDAP"
      description="Connect your organisation directory to sync users and authenticate against LDAP or Active Directory."
      icon={<Network size={18} className="text-white" />}
      iconClassName="bg-gradient-to-br from-cyan-500 to-teal-600"
      headerRight={
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold uppercase tracking-wide border border-amber-200">
          Coming soon
        </span>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
          LDAP configuration will be available in a later release. These fields
          are a preview of the setup form.
        </p>
        <label className="flex items-center gap-2 text-sm text-slate-500 cursor-not-allowed">
          <input
            type="checkbox"
            disabled
            className="rounded border-slate-300 text-cyan-600"
          />
          Enable LDAP authentication
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="LDAP host" placeholder="ldap.company.com" disabled />
          <Input label="Port" placeholder="389" disabled />
          <Input
            label="Bind DN"
            placeholder="cn=admin,dc=company,dc=com"
            disabled
          />
          <Input
            label="Bind password"
            type="password"
            placeholder="••••••••"
            disabled
          />
          <Input
            label="Base DN"
            placeholder="ou=users,dc=company,dc=com"
            disabled
          />
          <Input
            label="User search filter"
            placeholder="(uid={username})"
            disabled
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-500 cursor-not-allowed">
          <input
            type="checkbox"
            disabled
            className="rounded border-slate-300"
          />
          Use SSL / LDAPS (port 636)
        </label>
        <div className="flex justify-end pt-2 border-t border-slate-100">
          <Button disabled>Save LDAP settings</Button>
        </div>
      </div>
    </CollapsibleSettingsCard>
  );
}

export function TenantSettingsPage() {
  const { currentUser, showNotification } = useApp();
  const [profile, setProfile] = useState(null);
  const [q, setQ] = useState("");
  const allowedModules = currentUser?.modules || [];
  const hasPolicyStore = allowedModules.includes("policy_store");
  const hasGap = allowedModules.includes("gap");
  const hasBreach = allowedModules.includes("breach");
  const showAiProvider = hasPolicyStore || hasGap;
  const show = (keys) =>
    !q.trim() || keys.toLowerCase().includes(q.trim().toLowerCase());

  useEffect(() => {
    (async () => {
      try {
        const res = await _fetch("/auth/me/tenant-admin");
        if (res.ok) setProfile(await res.json());
      } catch {
        showNotification("Failed to load profile", "error");
      }
    })();
  }, [showNotification]);

  const data = profile ?? currentUser;

  const infoRows = [
    {
      label: "Full Name",
      value: data?.full_name,
      icon: <Users size={13} />,
      color: "text-blue-600 bg-blue-50",
    },
    {
      label: "Email",
      value: data?.email,
      icon: <Mail size={13} />,
      color: "text-violet-600 bg-violet-50",
    },
    {
      label: "Role",
      value: "Tenant Administrator",
      icon: <Shield size={13} />,
      color: "text-emerald-600 bg-emerald-50",
    },
    {
      label: "Organization Name",
      value:
        data?.organization_name ||
        currentUser?.organization_name ||
        currentUser?.company_name,
      icon: <Building2 size={13} />,
      color: "text-amber-600 bg-amber-50",
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div
        data-tour-id="tour-tenant-settings-header"
        className="flex flex-wrap items-center gap-3"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center shadow-md">
          <Lock size={18} className="text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-sm text-slate-400 flex items-center gap-1 mt-0.5">
            <Shield size={11} /> Account configuration
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search settings…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {show("profile account administrator organization name") && (
      <div data-tour-id="tour-tenant-settings-profile">
        <Card>
          {/* Banner */}
          <div className="h-10 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-800 mb-14 relative">
            <div className="absolute -bottom-10 left-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-800 flex items-center justify-center text-white text-2xl font-bold shadow-lg border-4 border-white">
                {(data?.full_name || data?.email || "?")[0].toUpperCase()}
              </div>
            </div>
          </div>

          <div className="px-2">
            <h2 className="text-lg font-bold text-slate-900">
              {data?.full_name ?? "—"}
            </h2>
            <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
              <Mail size={12} />
              {data?.email ?? "—"}
            </p>
            <span className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
              <Shield size={11} /> Tenant Administrator
            </span>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {infoRows.map(({ label, value, icon, color }) => (
              <div
                key={label}
                className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-100"
              >
                <span className={`p-2 rounded-lg ${color} flex-shrink-0`}>
                  {icon}
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">
                    {label}
                  </p>
                  <p className={`text-sm font-semibold text-slate-800 mt-0.5 truncate${label === "Email" ? " font-mono" : ""}`}>
                    {value ?? "—"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      )}

      {show("permission module access role department") && <ModuleAccessPanel />}

      {show("global compliance region country") && <GlobalComplianceSettingsPanel />}

      {show("smtp mail organization") && (
      <SmtpSettingsPanel
        apiBase="/tenant/settings"
        tourId="tour-tenant-settings-smtp"
        title="Organization SMTP"
        description="Configure your organization's mail server for user welcome and update notifications. When disabled, platform SMTP is used."
      />
      )}

      {show("email template branding") && (
      <CollapsibleSettingsCard
        tourId="tour-tenant-settings-email-branding"
        title="Email Template Branding"
        description="Customize the logo text, color scheme, copyright, and disclaimer for outgoing system emails."
        icon={<Mail size={18} className="text-white" />}
        iconClassName="bg-gradient-to-br from-rose-500 to-red-600"
        headerRight={
          <Link
            to="/tenant/email-templates"
            className="inline-flex items-center justify-center px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow transition"
          >
            Configure
          </Link>
        }
      />
      )}

      {show("email activities logs") && (
      <CollapsibleSettingsCard
        tourId="tour-tenant-settings-email-logs"
        title="Email Activities Logs"
        description="Track transaction status of sent welcome letters, updates, and testing alerts."
        icon={<Mail size={18} className="text-white" />}
        iconClassName="bg-gradient-to-br from-indigo-500 to-blue-600"
        headerRight={
          <Link
            to="/tenant/email-logs"
            className="inline-flex items-center justify-center px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow transition"
          >
            View Logs
          </Link>
        }
      />
      )}

      {show("policy generation prompt") && (
      <CollapsibleSettingsCard
        tourId="tour-tenant-settings-policy-prompt"
        title="Policy Generation Prompt"
        description="Customise the system prompt, sentence length, and monitor AI token usage for this organisation."
        icon={<Sparkles size={18} className="text-white" />}
        iconClassName="bg-gradient-to-br from-violet-500 to-indigo-600"
        headerRight={
          <Link
            to="/tenant/policy-prompt"
            className="inline-flex items-center justify-center px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow transition"
          >
            Configure
          </Link>
        }
      />
      )}

      {showAiProvider && show("ai provider configuration") && <AiProviderSettingsPanel />}

      {show("notification routing preferences") && <NotificationSettingsPanel />}

      {show("ldap directory") && <LdapSettingsCard />}

      {hasBreach && show("breach notification") && (
        <BreachSettingsPanel apiBase="/tenant/settings" />
      )}

      {hasBreach && show("breach template notice word document language translation") && (
        <BreachTemplatesPanel />
      )}

      {show("download organisation data export") && (
      <DataExportPanel
        tourId="tour-tenant-settings-data-export"
        apiBase="/tenant/settings/data-export"
        title="Download Organisation Data"
        description="Export every record of this organisation as CSV files, with an optional PDF report, so you keep a complete copy before any data is removed."
      />
      )}
    </div>
  );
}
